/**
 * offlineDb.js — IndexedDB wrapper dla trybu offline
 *
 * Object stores:
 *  - offline_games   : lokalne gry w toku / zakończone
 *  - sync_queue      : wyniki gier czekające na sync z serwerem
 *  - user_prefs      : ustawienia offline gracza
 */

const DB_NAME = 'kroniki-offline';
const DB_VERSION = 1;

let _db = null;

function openDb() {
  if (_db) return Promise.resolve(_db);

  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains('offline_games')) {
        const store = db.createObjectStore('offline_games', { keyPath: 'gameId' });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }

      if (!db.objectStoreNames.contains('sync_queue')) {
        const sq = db.createObjectStore('sync_queue', { keyPath: 'gameId' });
        sq.createIndex('synced', 'synced', { unique: false });
      }

      if (!db.objectStoreNames.contains('user_prefs')) {
        db.createObjectStore('user_prefs', { keyPath: 'key' });
      }
    };

    req.onsuccess = (e) => {
      _db = e.target.result;
      resolve(_db);
    };

    req.onerror = (e) => reject(e.target.error);
  });
}

function tx(storeName, mode = 'readonly') {
  return openDb().then((db) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    return { store, transaction };
  });
}

function promisifyRequest(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ─── Offline Games ─────────────────────────────────────────────────────────

/**
 * Zapisz / nadpisz stan gry offline
 * @param {object} gameState  - pełny stan gry (format zgodny z backendem)
 */
export async function saveGame(gameState) {
  const { store } = await tx('offline_games', 'readwrite');
  return promisifyRequest(
    store.put({
      gameId: gameState.gameId,
      createdAt: gameState.createdAt || Date.now(),
      updatedAt: Date.now(),
      status: gameState.status || 'active',
      gameState,
    })
  );
}

/**
 * Załaduj grę offline po ID
 */
export async function loadGame(gameId) {
  const { store } = await tx('offline_games');
  const record = await promisifyRequest(store.get(gameId));
  return record?.gameState ?? null;
}

/**
 * Lista wszystkich gier offline (bez pełnego stanu — lekkie)
 */
export async function listGames() {
  const { store } = await tx('offline_games');
  return new Promise((resolve, reject) => {
    const results = [];
    const cursor = store.openCursor();
    cursor.onsuccess = (e) => {
      const c = e.target.result;
      if (!c) return resolve(results);
      const { gameId, createdAt, updatedAt, status, gameState } = c.value;
      results.push({
        gameId,
        createdAt,
        updatedAt,
        status,
        playerName: gameState?.players?.[0]?.name ?? 'Gracz',
        turn: gameState?.currentTurn ?? 1,
      });
      c.continue();
    };
    cursor.onerror = () => reject(cursor.error);
  });
}

/**
 * Zakończ grę lokalnie i dodaj do kolejki synchronizacji
 */
export async function markGameFinished(gameId, result) {
  // Aktualizuj status gry
  const { store: gs } = await tx('offline_games', 'readwrite');
  const record = await promisifyRequest(gs.get(gameId));
  if (record) {
    record.status = 'finished';
    record.updatedAt = Date.now();
    await promisifyRequest(gs.put(record));
  }

  // Dodaj do kolejki sync
  await queueForSync(gameId, result);
}

/**
 * Usuń grę offline (po udanej synchronizacji lub manualnie)
 */
export async function deleteGame(gameId) {
  const { store } = await tx('offline_games', 'readwrite');
  return promisifyRequest(store.delete(gameId));
}

// ─── Sync Queue ────────────────────────────────────────────────────────────

/**
 * Dodaj wynik gry do kolejki synchronizacji
 * @param {string} gameId
 * @param {object} result  - { winner, turns, players: [{name, score, famePoints}] }
 */
export async function queueForSync(gameId, result) {
  const { store } = await tx('sync_queue', 'readwrite');
  return promisifyRequest(
    store.put({
      gameId,
      finishedAt: Date.now(),
      result,
      synced: false,
      attempts: 0,
    })
  );
}

/**
 * Pobierz wszystkie niezsynced rekordy
 */
export async function getPendingSyncs() {
  const { store } = await tx('sync_queue');
  return new Promise((resolve, reject) => {
    const results = [];
    const req = store.getAll();
    req.onsuccess = () => {
      resolve((req.result || []).filter(r => !r.synced));
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Oznacz rekord jako zsynchronizowany
 */
export async function markSynced(gameId) {
  const { store } = await tx('sync_queue', 'readwrite');
  const record = await promisifyRequest(store.get(gameId));
  if (record) {
    record.synced = true;
    await promisifyRequest(store.put(record));
  }
}

/**
 * Zwiększ licznik prób synchronizacji (przy błędzie)
 */
export async function incrementSyncAttempts(gameId) {
  const { store } = await tx('sync_queue', 'readwrite');
  const record = await promisifyRequest(store.get(gameId));
  if (record) {
    record.attempts = (record.attempts || 0) + 1;
    await promisifyRequest(store.put(record));
  }
}

// ─── User Prefs ────────────────────────────────────────────────────────────

export async function getPref(key) {
  const { store } = await tx('user_prefs');
  const record = await promisifyRequest(store.get(key));
  return record?.value ?? null;
}

export async function setPref(key, value) {
  const { store } = await tx('user_prefs', 'readwrite');
  return promisifyRequest(store.put({ key, value }));
}
